import { useState } from "react";
import axios from "axios";
import { Search, LocateFixed, Loader2, MapPin, Home, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COVERINGS, REGIONS, matchRegion } from "@/lib/catalog";
import { Section, Field, NumField } from "./ui";

import { tr } from "@/lib/i18n";
const NOMINATIM = "https://nominatim.openstreetmap.org";

const RoofTypeCard = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`text-left p-4 rounded-lg border-2 transition-colors ${active ? "border-[#0F52BA] bg-blue-50/60" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
    <Icon className={`w-6 h-6 mb-2 ${active ? "text-[#0F52BA]" : "text-slate-400"}`} />
    <div className="font-semibold text-slate-900">{title}</div>
    <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
  </button>
);

export default function StepProject({ p, update }) {
  const [q, setQ] = useState(p.address || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const applyPlace = async (r) => {
    const a = r.address || {};
    const lat = Number(r.lat), lng = Number(r.lon);
    let altitude = p.altitude;
    try {
      const { data } = await axios.get(`https://api.open-meteo.com/v1/elevation?latitude=${lat}&longitude=${lng}`);
      altitude = Math.round(data.elevation?.[0] ?? altitude);
    } catch { /* altitudine manuale */ }
    const hadRoof = (p.roofs || []).length > 0;
    update((prev) => ({
      address: r.display_name, lat, lng, altitude,
      region: matchRegion(a.state || "") || prev.region,
      province: a.county || a.province || a.city || a.town || "",
      env: {},
      roofs: [],
    }));
    setQ(r.display_name);
    setResults([]);
    if (hadRoof) toast.info(tr("Indirizzo cambiato: il disegno del tetto è stato azzerato"));
  };

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${NOMINATIM}/search`, { params: { format: "json", addressdetails: 1, countrycodes: "it,ch,de,at", limit: 6, q } });
      setResults(data.filter((r, i, a) => a.findIndex((x) => x.display_name === r.display_name) === i));
      if (!data.length) toast.info(tr("Nessun indirizzo trovato"));
    } catch { toast.error(tr("Ricerca indirizzo non disponibile")); }
    setBusy(false);
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error(tr("Geolocalizzazione non supportata"));
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const { data } = await axios.get(`${NOMINATIM}/reverse`, { params: { format: "json", addressdetails: 1, lat: coords.latitude, lon: coords.longitude } });
        await applyPlace({ ...data, lat: coords.latitude, lon: coords.longitude });
      } catch { toast.error(tr("Indirizzo non determinabile")); }
      setBusy(false);
    }, () => { setBusy(false); toast.error(tr("Posizione non disponibile")); });
  };

  const setRoofType = (t) => update((prev) => ({
    roof_type: t,
    covering: COVERINGS[t][0].v,
    modules: { ...prev.modules, system: t === "piano" ? "zavorra" : "binari" },
    roofs: (prev.roofs || []).map((r) => ({ ...r, removed: [] })),
  }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-6">
      <div className="sr-rise">
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{tr("Dati di progetto")}</h1>
        <p className="text-slate-500 mt-2">{tr("Inserisci i riferimenti del cliente e individua l'edificio.")}</p>
      </div>
      <Section kicker="01" title={tr("Anagrafica")}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={tr("Nome progetto")}><Input data-testid="project-name-input" className="h-11" value={p.name} onChange={(e) => update({ name: e.target.value })} /></Field>
          <Field label={tr("Cliente")}><Input data-testid="project-client-input" className="h-11" value={p.client} onChange={(e) => update({ client: e.target.value })} placeholder={tr("Nome e cognome / ragione sociale")} /></Field>
        </div>
      </Section>
      <Section kicker="02" title={tr("Ubicazione")}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="address-search-input" className="h-11 pl-9" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={tr("Via, numero civico, comune")} />
          </div>
          <Button data-testid="address-search-btn" onClick={search} disabled={busy} className="h-11 bg-[#0F52BA] hover:bg-[#0D47A1]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="ml-1.5 hidden sm:inline">{tr("Cerca")}</span>
          </Button>
          <Button data-testid="geolocate-btn" variant="outline" onClick={locate} className="h-11" title={tr("Usa la mia posizione")}><LocateFixed className="w-4 h-4" /></Button>
        </div>
        {results.length > 0 && (
          <ul className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="address-results">
            {results.map((r, i) => (
              <li key={r.place_id}>
                <button data-testid={`address-result-${i}`} onClick={() => applyPlace(r)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors">{r.display_name}</button>
              </li>
            ))}
          </ul>
        )}
        {p.lat != null && (
          <div className="mt-3 text-xs font-tech text-slate-500" data-testid="project-coords">LAT {p.lat.toFixed(5)} {tr("· LNG")} {p.lng.toFixed(5)}</div>
        )}
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <Field label={tr("Regione")}>
            <Select value={p.region || undefined} onValueChange={(v) => update({ region: v, env: {} })}>
              <SelectTrigger className="h-11" data-testid="region-select"><SelectValue placeholder={tr("Seleziona")} /></SelectTrigger>
              <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={tr("Provincia")}><Input data-testid="province-input" className="h-11" value={p.province} onChange={(e) => update({ province: e.target.value, env: {} })} /></Field>
          <NumField label={tr("Altitudine")} unit={tr("m s.l.m.")} value={p.altitude} testid="altitude-input" onChange={(v) => update({ altitude: v, env: {} })} />
        </div>
      </Section>
      <Section kicker="03" title={tr("Edificio e copertura")}>
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <RoofTypeCard testid="roof-type-falda" active={p.roof_type === "falda"} onClick={() => setRoofType("falda")} icon={Home} title={tr("Tetto a falda")} desc={tr("Binari con ganci di ancoraggio")} />
          <RoofTypeCard testid="roof-type-piano" active={p.roof_type === "piano"} onClick={() => setRoofType("piano")} icon={Building2} title={tr("Tetto piano")} desc={tr("Sistema zavorrato 10° / 15°")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumField label={tr("Altezza dell'edificio")} unit="m" value={p.building_height} testid="building-height-input" onChange={(v) => update({ building_height: v, env: {} })} />
          <Field label={tr("Copertura")}>
            <Select value={p.covering} onValueChange={(v) => update({ covering: v })}>
              <SelectTrigger className="h-11" data-testid="covering-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COVERINGS[p.roof_type].map((c) => <SelectItem key={c.v} value={c.v}>{tr(c.l)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
      </Section>
    </div>
  );
}
